import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Button,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverFooter,
  PopoverBody,
  Text,
  VStack,
  HStack,
  Center,
  Divider,
  Spacer,
  PlacementWithLogical,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import colors from "app/colors";
import uniq from "lodash/uniq";
import { CloseIcon } from "modules/icons";
import { GroupedVirtuoso } from "react-virtuoso";

interface IProps {
  title: string;
  saveTitle: string;
  description?: string;
  sections: {
    title: string;
    ids: string[];
  }[];
  placement?: PlacementWithLogical;
  children: ReactNode;
  renderRow: (
    sectionIndex: number,
    rowIndex: number,
    id: string,
  ) => React.ReactNode;
  onSaveClicked: (ids: string[]) => void;
  saveButtonWidth?: number;
  initialSelectedIds?: string[];
}

const CopyListPopover: React.FC<IProps> = ({
  title,
  saveTitle,
  description,
  sections,
  placement,
  children,
  renderRow,
  onSaveClicked,
  saveButtonWidth,
  initialSelectedIds,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialSelectedIds ?? [],
  );

  useEffect(() => {
    setSelectedIds(initialSelectedIds ?? []);
  }, [initialSelectedIds]);

  const allIds = useMemo(
    () =>
      sections.reduce(
        (acc, section) => [...acc, ...section.ids],
        [] as string[],
      ),
    [sections],
  );

  const onSelectAllClicked = useCallback(() => {
    setSelectedIds(allIds);
  }, [allIds]);

  const onClearSelectionClicked = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const onSelectRowClicked = useCallback(
    (id: string) => () => {
      setSelectedIds((state) =>
        state.includes(id)
          ? [...state.filter((tempId) => tempId !== id)]
          : uniq([...state, id]),
      );
    },
    [],
  );

  const _onSaveClicked = useCallback(() => {
    onSaveClicked(selectedIds);
    setSelectedIds([]);
    onClose();
  }, [onSaveClicked, selectedIds, onClose]);

  const renderGroupHeader = useCallback(
    (sectionIndex: number) => {
      const section = sections[sectionIndex];
      return (
        <Text
          fontSize={14}
          color={colors.grayMain}
          pt={sectionIndex > 0 ? 4 : 0}
          bg={colors.white}
        >
          {section.title}
        </Text>
      );
    },
    [sections],
  );

  const renderItem = useCallback(
    (index: number, sectionIndex: number) => {
      const id = allIds[index];
      return (
        <>
          <HStack>
            <Checkbox
              isChecked={selectedIds.includes(id) === true}
              onChange={onSelectRowClicked(id)}
            />
            {renderRow(sectionIndex, index, id)}
          </HStack>
          {sectionIndex < allIds.length - 1 && <Divider />}
        </>
      );
    },
    [allIds, onSelectRowClicked, renderRow, selectedIds],
  );

  return (
    <Popover
      placement={placement}
      isLazy
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent maxH="50vh" w="282px" minW="200px" boxShadow="lg">
          <PopoverArrow />
          <PopoverHeader pb={1} px={0} mx={2}>
            <HStack w="full">
              <Text
                fontFamily="Outfit"
                fontSize={14}
                fontWeight={700}
                color={colors.blackSecondary}
              >
                {title}
              </Text>
              <Spacer />
              <Button
                variant="link"
                fontSize={11}
                fontWeight={600}
                color={colors.blue}
                onClick={onSelectAllClicked}
              >
                Select All
              </Button>
            </HStack>
          </PopoverHeader>
          <PopoverBody overflowY="auto" fontWeight="normal" px={2} py={0}>
            <VStack align="stretch" spacing={1}>
              {description && (
                <Text
                  fontSize="sm"
                  color={colors.gray50}
                  textAlign="center"
                  maxW={500}
                  fontWeight={500}
                >
                  {description}
                </Text>
              )}
              <HStack>
                <Spacer />
                {selectedIds.length > 0 && (
                  <Button
                    variant="link"
                    fontSize="sm"
                    fontWeight={600}
                    color={colors.gray40}
                    onClick={onClearSelectionClicked}
                    leftIcon={
                      <CloseIcon
                        boxSize={4}
                        marginRight={-2}
                        fill={colors.gray40}
                      />
                    }
                  >
                    Clear
                  </Button>
                )}
              </HStack>
              <GroupedVirtuoso
                style={{ minHeight: "200px" }}
                groupCounts={sections.map((section) => section.ids.length)}
                groupContent={renderGroupHeader}
                itemContent={renderItem}
              />
            </VStack>
          </PopoverBody>
          <PopoverFooter border="none">
            <Center>
              <Button
                px={8}
                variant="hover"
                bg={colors.darkBlue}
                color={colors.white}
                fontSize={12}
                fontWeight={700}
                w={saveButtonWidth}
                size="sm"
                onClick={_onSaveClicked}
                isDisabled={selectedIds.length === 0}
              >
                {saveTitle}
              </Button>
            </Center>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(CopyListPopover);
